import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { Box, Button } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { styled } from "@mui/material/styles";
import { KeyboardEvent, ReactNode, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import VerticalTabsArticle from "../VerticalTabsArticle/VerticalTabsArticle";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .Platform-x-Paper-root": {
    height: "-webkit-fill-available",
    maxWidth: "none",
    width: "100%",

    maxHeight: "100%",
    margin: "0px",
    [`@media (min-width:${theme.breakpoints.values.xs}px)`]: {
      margin: "0px",
    },
    [`@media (min-width:${theme.breakpoints.values.sm}px)`]: {
      margin: "20px",
    },
  },
}));

export interface DialogTitleProps {
  id: string;
  children?: ReactNode;
  /** Closes the dialog KEEPING every edit — Done, the back arrow, Escape and the backdrop. */
  onClose: () => void;
  /** Reverts every edit made since the dialog opened, then closes. */
  onCancel?: () => void;
}

const BootstrapDialogTitle = (props: DialogTitleProps) => {
  const { children, onClose, onCancel, ...other } = props;
  const { t } = useTranslation();

  // The back arrow is a plain Box: without this it cannot be reached or used from the keyboard.
  const onBackKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClose();
    }
  };

  return (
    <DialogTitle
      sx={{
        m: 0,
        p: 2,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        fontSize: "20px",
        fontWeight: 600,
      }}
      {...other}>
      <Box
        onClick={onClose}
        onKeyDown={onBackKeyDown}
        role='button'
        tabIndex={0}
        aria-label={t("back")}
        data-testid='publish-socialshare-close'
        className='backspace'
        sx={{
          position: "absolute",
          left: 15,
        }}>
        <KeyboardBackspaceIcon />
      </Box>
      {children}

      <Box
        sx={{
          position: "absolute",
          right: 15,
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}>
        {onCancel ? (
          <Button
            onClick={onCancel}
            data-testid='publish-socialshare-cancel'
            className='sm'
            variant='secondaryButton'>
            {t("cancel")}
          </Button>
        ) : null}
        <Button
          onClick={onClose}
          data-testid='publish-socialshare-close'
          className='sm'
          variant='primaryButton'>
          {t("done")}
        </Button>
      </Box>
    </DialogTitle>
  );
};

export default function PublishSocialShare({
  open,
  handleClose,
  state,
  setState,
  socialOgTags,
  setSocialOgTags,

  updateStructureDataArticle,
  slugField,
}) {
  const { t } = useTranslation();

  // Every tab edits the article live through `setState`, so "Cancel" has nothing to roll back unless
  // the state is captured when the dialog opens. The snapshot is taken ONCE per opening (not on every
  // edit) and holds both pieces of state this dialog can change: the article instance and the Social
  // Share tags, which SocialShare keeps in a separate state.
  const snapshot = useRef<{ state: unknown; socialOgTags: unknown } | null>(null);
  useEffect(() => {
    if (open) snapshot.current = { state, socialOgTags };
    // Deliberately keyed on `open` alone: re-capturing on each edit would make Cancel a no-op.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleCancel = () => {
    if (snapshot.current) {
      setState(snapshot.current.state);
      setSocialOgTags?.(snapshot.current.socialOgTags);
    }
    // A reverted slug must not keep a stale "available / not unique" status from before the revert.
    slugField?.invalidate?.();
    handleClose();
  };

  return (
    <Box sx={{ width: "100%" }}>
      <BootstrapDialog onClose={handleClose} aria-labelledby='customized-dialog-title' open={open}>
        <BootstrapDialogTitle
          id='customized-dialog-title'
          onClose={handleClose}
          onCancel={handleCancel}>
          {t("settings")}
        </BootstrapDialogTitle>
        <DialogContent
          dividers
          sx={{
            display: "flex",
            justifyContent: "center",
            ".Platform-x-DialogContent-root": {
              padding: "50px",
            },
          }}>
          <Box sx={{ width: { md: "836px", xs: "100%" } }}>
            <VerticalTabsArticle
              state={state}
              setState={setState}
              socialOgTags={socialOgTags}
              setSocialOgTags={setSocialOgTags}
              updateStructureDataArticle={updateStructureDataArticle}
              slugField={slugField}
            />
          </Box>
        </DialogContent>
      </BootstrapDialog>
    </Box>
  );
}
